/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IComponentStyles } from '@fluentui/foundation-legacy';
import { ChoiceGroup } from '@fluentui/react/lib/ChoiceGroup';
import { IImageProps, IImageStyles, Image } from '@fluentui/react/lib/Image';
import { Stack } from '@fluentui/react/lib/Stack';
import { FontWeights } from '@fluentui/react/lib/Styling';
import { ITextProps, ITextSlots } from '@fluentui/react/lib/Text';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { Property } from 'csstype';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getAssetsUrl } from 'src/core/utils/api/getAssetsUrl';
import { CustomTextProjectKind } from 'src/customText/core/stores/CustomTextProjectStore';
import { ModalFooter } from 'src/customText/verticals/projectCreationModal/components/ModalFooter';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import {
    projectCreationContentLeftMargin,
    ProjectCreationSteps,
    ProjectInfoDto
} from 'src/customText/verticals/projectCreationModal/ProjectCreationModal';

export type ClassificationProjectTypeScreenProps = {
    onDismiss: () => void;
    onPrevious: () => void;
    projectDto: ProjectInfoDto;
    onSubmit: (projectKind: CustomTextProjectKind) => void;
};

const messages = defineMessages({
    title: {
        id: 'customText.classificationProjectTypeScreen.title',
        defaultMessage: 'Select project type'
    },
    description: {
        id: 'customText.classificationProjectTypeScreen.description',
        defaultMessage:
            'Select the type of custom text classification project that you would like to create. This project type cannot be changed later.'
    },
    learnMoreLink: {
        id: 'customText.classificationProjectTypeScreen.learnMoreLink',
        defaultMessage: 'Learn more about classification types'
    },
    singleClassificationOption: {
        id: 'customText.classificationProjectTypeScreen.singleClassificationOption',
        defaultMessage: 'Single label classification - each file will have only one label'
    },
    singleClassificationExample: {
        id: 'customText.classificationProjectTypeScreen.singleClassificationExample',
        defaultMessage: 'For example, file 1 is classified as A and file 2 is classified as B'
    },
    multiClassificationOption: {
        id: 'customText.classificationProjectTypeScreen.multiClassificationOption',
        defaultMessage: 'Multi label classification - each file can have one or many labels'
    },
    multiClassificationExample: {
        id: 'customText.classificationProjectTypeScreen.multiClassificationExample',
        defaultMessage: 'For example, file 1 is classified as A, B, and C and file 2 is classified as B and C'
    },
    singleClassificationImageTitle: {
        id: 'customText.classificationProjectTypeScreen.singleClassificationImageTitle',
        defaultMessage: 'Single label classification image'
    },
    multiClassificationImageTitle: {
        id: 'customText.classificationProjectTypeScreen.multiClassificationImageTitle',
        defaultMessage: 'Multi label classification image'
    }
});

const imageStyles = classNamesFunction<IImageProps, IImageStyles>()({ root: { marginLeft: 55 } });

const labelStyles = classNamesFunction<ITextProps, IComponentStyles<ITextSlots>>()({
    root: { fontStyle: 'italic', paddingLeft: '26px', marginBottom: '8px', paddingBottom: '8px' }
});

export const ClassificationProjectTypeScreen = observer((props: ClassificationProjectTypeScreenProps) => {
    const { onSubmit, onDismiss, onPrevious, projectDto } = props;
    const { localize } = useLocalization();
    const [projectKind, setProjectKind] = React.useState<CustomTextProjectKind>(projectDto.projectKind);
    const { isLargeOrGreater } = useResponsiveMode();

    return (
        <Stack>
            <Stack
                styles={{ root: { maxHeight: '400px', overflowY: 'auto' } }}
                tokens={{ childrenGap: 24, padding: `${isLargeOrGreater ? `0 0 0 ${projectCreationContentLeftMargin}` : '12px 20px'}` }}>
                <Stack tokens={{ childrenGap: 6 }}>
                    <LuisText variant="xLarge" fontWeight={FontWeights.semibold as Property.FontWeight}>
                        {localize(messages.title)}
                    </LuisText>
                    <LuisText>
                        {localize(messages.description)}
                        &nbsp;
                        <ExternalLink href="https://aka.ms/ctc-project-types">{localize(messages.learnMoreLink)}</ExternalLink>
                    </LuisText>
                </Stack>

                <Stack tokens={{ childrenGap: 8 }}>
                    <ChoiceGroup
                        selectedKey={projectKind}
                        options={[
                            {
                                key: 'CustomSingleLabelClassification',
                                text: localize(messages.singleClassificationOption),
                                onRenderField: (rendererProps, renderer) => (
                                    <Stack>
                                        <LuisText>{renderer(rendererProps)}</LuisText>
                                        <LuisText styles={labelStyles}>{localize(messages.singleClassificationExample)}</LuisText>
                                        <Image
                                            width={100}
                                            styles={imageStyles}
                                            alt={localize(messages.singleClassificationImageTitle)}
                                            src={getAssetsUrl('assets/images/customText/singleClassificationLogo.svg')}
                                        />
                                    </Stack>
                                )
                            },
                            {
                                key: 'CustomMultiLabelClassification',
                                text: localize(messages.multiClassificationOption),
                                onRenderField: (rendererProps, renderer) => (
                                    <Stack>
                                        <LuisText>{renderer(rendererProps)}</LuisText>
                                        <LuisText styles={labelStyles}>{localize(messages.multiClassificationExample)}</LuisText>
                                        <Image
                                            width={100}
                                            styles={imageStyles}
                                            alt={localize(messages.multiClassificationImageTitle)}
                                            src={getAssetsUrl('assets/images/customText/multiClassificationLogo.svg')}
                                        />
                                    </Stack>
                                )
                            }
                        ]}
                        onChange={(_, o) => setProjectKind(o.key as CustomTextProjectKind)}
                    />
                </Stack>
            </Stack>

            <ModalFooter
                isValid={Boolean(projectKind)}
                onDismiss={onDismiss}
                onPrevious={onPrevious}
                onNext={() => onSubmit(projectKind)}
                activeStep={ProjectCreationSteps.ProjectType}
            />
        </Stack>
    );
});
